import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { searchTerms, termSearchPattern, TOURNAMENT_TERMS } from "@bw/shared/tournaments";
import type { TournamentTermKey } from "@bw/shared/tournaments";
import { createContext, Fragment, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { DenseButton, NewBadge, SetupCard, STAFF_FONT, tone } from "./ui";

type GlossaryApi = { open: (query?: string) => void };
const GlossaryContext = createContext<GlossaryApi | null>(null);

export function useGlossary(): GlossaryApi | null {
  return useContext(GlossaryContext);
}

function highlight(text: string, query: string): ReactNode {
  const pattern = termSearchPattern(query);
  if (!pattern) return text;
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    parts.push(text.slice(last, index));
    parts.push(
      <Box component="mark" key={index} sx={{ bgcolor: tone.brandMark, color: "inherit", borderRadius: "2px" }}>
        {text.slice(index, index + query.trim().length)}
      </Box>,
    );
    last = index + query.trim().length;
  }
  parts.push(text.slice(last));
  return parts.map((part, index) => <Fragment key={index}>{part}</Fragment>);
}

/** The searchable "Tournament terms" panel (F3). */
export function GlossaryPanel({
  initialQuery = "",
  onClose,
  sx,
}: {
  initialQuery?: string;
  onClose?: () => void;
  sx?: object;
}) {
  const [query, setQuery] = useState(initialQuery);
  const matches = useMemo(() => searchTerms(query), [query]);
  const hitKeys = new Set<TournamentTermKey>(query.trim() ? matches.map((term) => term.key) : []);
  return (
    <SetupCard
      actions={onClose ? <DenseButton onClick={onClose} size="sm">Close</DenseButton> : undefined}
      sx={{ display: "flex", flexDirection: "column", overflow: "hidden", ...sx }}
      title="Tournament terms"
    >
      <Box sx={{ p: "10px 14px 6px" }}>
        <Box component="label" htmlFor="glossary-search" sx={{ position: "absolute", left: -9999 }}>
          Search terms
        </Box>
        <Box
          component="input"
          id="glossary-search"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          placeholder="Search terms"
          sx={{
            height: 34,
            boxSizing: "border-box",
            width: "100%",
            border: `1px solid ${tone.line}`,
            borderRadius: "6px",
            px: "10px",
            font: "inherit", fontWeight: 500, fontSize: 13.5,
            outline: "none",
            "&:focus": { borderColor: tone.brand, boxShadow: `0 0 0 3px ${tone.brandRing}` },
          }}
          value={query}
        />
        <Box aria-live="polite" role="status" sx={{ fontSize: 11.5, color: tone.subtle, mt: "5px" }}>
          {query.trim()
            ? `${matches.length} of ${TOURNAMENT_TERMS.length} terms match “${query.trim()}”`
            : `${TOURNAMENT_TERMS.length} terms`}
        </Box>
      </Box>
      <Box sx={{ overflowY: "auto", flexGrow: 1, minHeight: 0 }}>
        {(query.trim() ? matches : []).map((term) => (
          <Box key={term.key} sx={{ p: "9px 14px", borderTop: `1px solid ${tone.divider}` }}>
            <Box component="b" sx={{ display: "block", fontSize: 13 }}>
              {highlight(term.label, query)}
            </Box>
            <Box component="span" sx={{ display: "block", fontSize: 12.5, color: tone.muted, lineHeight: 1.45, mt: "2px" }}>
              {highlight(term.staff, query)}
              {term.key === "seed" ? (
                <Box component="span" sx={{ ml: 0.5 }}>
                  <NewBadge />
                </Box>
              ) : null}
            </Box>
          </Box>
        ))}
        <Box sx={{ p: "10px 14px", borderTop: `1px solid ${tone.divider}` }}>
          <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, mb: "6px" }}>ALL TERMS</Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
            {[...TOURNAMENT_TERMS]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((term) => {
                const hit = hitKeys.has(term.key);
                return (
                  <Box
                    component="button"
                    key={term.key}
                    onClick={() => setQuery(term.label)}
                    sx={{
                      height: 24,
                      display: "inline-flex",
                      alignItems: "center",
                      px: "8px",
                      borderRadius: "12px",
                      border: `1px solid ${hit ? tone.brandHi : tone.line}`,
                      bgcolor: hit ? tone.brandSoft : tone.surface,
                      color: hit ? tone.brandText : tone.ink2,
                      font: "inherit", fontWeight: 600, fontSize: 11.5,
                      cursor: "pointer",
                    }}
                    type="button"
                  >
                    {term.label}
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>
      <Box sx={{ p: "10px 14px", fontSize: 12, color: tone.subtle, borderTop: `1px solid ${tone.divider}` }}>
        Every ⓘ in setup links here. Players see a player-worded version of each term on the event page.
      </Box>
    </SetupCard>
  );
}

export function GlossaryDrawer({
  open,
  onClose,
  initialQuery,
}: {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
}) {
  return (
    <Drawer anchor="right" onClose={onClose} open={open} slotProps={{ paper: { sx: { width: { xs: "100%", sm: 400 }, p: 1.5, bgcolor: tone.page } } }}>
      {open ? <GlossaryPanel initialQuery={initialQuery} onClose={onClose} sx={{ height: "100%" }} /> : null}
    </Drawer>
  );
}

/** Lets any ⓘ open the glossary; pages render one provider. */
export function GlossaryProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ open: boolean; query: string }>({ open: false, query: "" });
  const open = useCallback((query = "") => setState({ open: true, query }), []);
  const api = useMemo(() => ({ open }), [open]);
  return (
    <GlossaryContext.Provider value={api}>
      {children}
      <GlossaryDrawer initialQuery={state.query} onClose={() => setState((s) => ({ ...s, open: false }))} open={state.open} />
    </GlossaryContext.Provider>
  );
}
